import React, { useState, useMemo } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { FormInput, FormCombobox, FormTextarea } from "../FormComponents";
import { useCreateRecommendation, useUpdateRecommendation, useListRecommendations, RecommendationGroup } from "@/lib/api/risk/recommendation";
import { validateRecommendationRules, RecommendationRuleInput, RangeType } from "./validation";
import { AcceptedCommodity } from "@/lib/api/risk/profiles/commodities";
import { CommodityOptions } from "@/lib/api/commodity";
import { CropYearSelect } from "../CropYearSelect";
import { ValidationError } from "@/lib/responseResults";
import { Trash2, Plus, ChevronDown, ChevronRight } from "lucide-react";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";

const RangeTypeOptions = [
  { value: "<", label: "< (Less than)" },
  { value: "<=", label: "<= (Less than or equal)" },
  { value: ">", label: "> (Greater than)" },
  { value: ">=", label: ">= (Greater than or equal)" },
  { value: "between", label: "Between (e.g., 10-20)" },
];

const ConstraintTypeOptions = [
  { value: "hedged_percent", label: "Hedged Percent" },
];

// Helper: Parse range constraint value into numeric range
function parseRangeForFiltering(rangeType: RangeType, value: string): { min: number | null; max: number | null; minInclusive: boolean; maxInclusive: boolean } | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (rangeType === "between") {
    const parts = trimmed.split("-");
    if (parts.length !== 2) return null;
    const min = parseFloat(parts[0].trim());
    const max = parseFloat(parts[1].trim());
    if (isNaN(min) || isNaN(max) || min >= max) return null;
    return { min, max, minInclusive: true, maxInclusive: true };
  } else {
    const numValue = parseFloat(trimmed);
    if (isNaN(numValue)) return null;

    switch (rangeType) {
      case "<":
        return { min: null, max: numValue, minInclusive: true, maxInclusive: false };
      case "<=":
        return { min: null, max: numValue, minInclusive: true, maxInclusive: true };
      case ">":
        return { min: numValue, max: null, minInclusive: false, maxInclusive: true };
      case ">=":
        return { min: numValue, max: null, minInclusive: true, maxInclusive: true };
      default:
        return null;
    }
  }
}

// Helper: Check if two ranges overlap
function rangesOverlap(range1: { min: number | null; max: number | null; minInclusive: boolean; maxInclusive: boolean }, range2: { min: number | null; max: number | null; minInclusive: boolean; maxInclusive: boolean }): boolean {
  const min1 = range1.min ?? -Infinity;
  const max1 = range1.max ?? Infinity;
  const min2 = range2.min ?? -Infinity;
  const max2 = range2.max ?? Infinity;

  if (max1 < min2) return false;
  if (max1 === min2) {
    if (!range1.maxInclusive || !range2.minInclusive) return false;
  }

  if (max2 < min1) return false;
  if (max2 === min1) {
    if (!range2.maxInclusive || !range1.minInclusive) return false;
  }

  return true;
}

// Helper: Format constraint type for display
function formatConstraintType(constraintType: string): string {
  return constraintType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Helper: Format rule for preview
function formatRulePreview(rule: { rangeType: string; constraintType: string; rangeConstraintValue: string; message: string }): string {
  if (!rule.rangeConstraintValue || !rule.message) return "";

  const constraint = formatConstraintType(rule.constraintType);
  let rangeText = "";

  if (rule.rangeType === "between") {
    rangeText = `is between ${rule.rangeConstraintValue.replace("-", " and ")}`;
  } else {
    rangeText = `${rule.rangeType} ${rule.rangeConstraintValue}`;
  }

  return `If ${constraint} ${rangeText} then "${rule.message}"`;
}

const RuleFormSchema = z.object({
  recommendationDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format"),
  cropYear: z.number().int(),
  commodityType: z.string().min(1, "Commodity type cannot be empty"),
  rules: z.array(
    z.object({
      rangeType: z.enum(["<", "<=", ">", ">=", "between"]),
      constraintType: z.enum(["hedged_percent"]),
      rangeConstraintValue: z.string().min(1, "Range constraint value cannot be empty"),
      message: z.string().min(1, "Message cannot be empty").trim(),
    })
  ).min(1, "At least one rule is required"),
});

type FormData = z.infer<typeof RuleFormSchema>;

interface RecommendationFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  initialDate?: string;
  initialCommodity?: string;
  initialCropYear?: number;
  existingGroupId?: string; // If provided, this is an update operation
  existingGroup?: RecommendationGroup; // If provided, populate form with existing rules
}

export function RecommendationForm({
  onSuccess,
  onCancel,
  showCancelButton = true,
  initialDate,
  initialCommodity,
  initialCropYear,
  existingGroupId,
  existingGroup,
}: RecommendationFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set([0]));

  const createRecommendation = useCreateRecommendation();
  const updateRecommendation = useUpdateRecommendation();

  const defaultDate = initialDate || moment().format("YYYY-MM-DD");
  const defaultCropYear = initialCropYear || parseInt(moment().format("YYYY"));

  // Get all commodity options
  const commodityOptions = CommodityOptions;

  // Default to first commodity if none specified
  const defaultCommodity = existingGroup?.commodity_type || initialCommodity || commodityOptions[0]?.value || "";

  // Prepare initial rules from existing group if editing
  const initialRules = React.useMemo(() => {
    if (existingGroup && existingGroup.RecommendationRules.length > 0) {
      return existingGroup.RecommendationRules.map((rule) => ({
        rangeType: rule.range_type,
        constraintType: rule.constraint_type,
        rangeConstraintValue: rule.range_constraint_value,
        message: rule.message,
      }));
    }
    return [
      {
        rangeType: "<=" as const,
        constraintType: "hedged_percent" as const,
        rangeConstraintValue: "",
        message: "",
      },
    ];
  }, [existingGroup]);

  // Fetch existing recommendations for the same date/commodity/cropYear to check for overlaps
  const form = useForm<FormData>({
    resolver: zodResolver(RuleFormSchema),
    mode: "onChange",
    defaultValues: {
      recommendationDate: existingGroup
        ? moment.tz(existingGroup.recommendation_date, "UTC").format("YYYY-MM-DD")
        : defaultDate,
      cropYear: existingGroup?.crop_year || defaultCropYear,
      commodityType: defaultCommodity,
      rules: initialRules,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "rules",
  });

  const watchedDate = form.watch("recommendationDate");
  const watchedCommodity = form.watch("commodityType");
  const watchedCropYear = form.watch("cropYear");
  const watchedRules = form.watch("rules");

  // Fetch existing recommendations for validation
  const { data: existingData } = useListRecommendations({
    date: watchedDate,
    commodityType: watchedCommodity,
    cropYear: watchedCropYear,
  });

  // Get existing rules for the same group (excluding current group if updating)
  const existingRules = React.useMemo(() => {
    if (!existingData?.data?.data) return [];

    return existingData.data.data
      .filter((group: RecommendationGroup) => {
        // If updating, exclude the current group
        if (existingGroupId && group.id === existingGroupId) {
          return false;
        }
        return (
          group.commodity_type === watchedCommodity &&
          group.crop_year === watchedCropYear
        );
      })
      .flatMap((group: RecommendationGroup) =>
        group.RecommendationRules.map((rule) => ({
          rangeType: rule.range_type,
          constraintType: rule.constraint_type,
          rangeConstraintValue: rule.range_constraint_value,
          message: rule.message,
        }))
      );
  }, [existingData, watchedCommodity, watchedCropYear, existingGroupId]);

  // Get existing commodity/crop year pairs for the selected date
  const existingPairs = React.useMemo(() => {
    if (!existingData?.data?.data) return new Set<string>();

    return new Set(
      existingData.data.data
        .filter((group: RecommendationGroup) => {
          // If updating, exclude the current group
          if (existingGroupId && group.id === existingGroupId) {
            return false;
          }
          return true;
        })
        .map((group: RecommendationGroup) => `${group.commodity_type}|${group.crop_year}`)
    );
  }, [existingData, existingGroupId]);

  // Filter commodity options to exclude those already paired with the current crop year
  const filteredCommodityOptions = commodityOptions.filter((option) => {
    const pairKey = `${option.value}|${watchedCropYear}`;
    return !existingPairs.has(pairKey);
  });

  // Run validation on every render
  try {
    // Convert form data to RecommendationRuleInput format
    const rules: RecommendationRuleInput[] = watchedRules.map((rule) => ({
      rangeType: rule.rangeType,
      constraintType: rule.constraintType,
      rangeConstraintValue: rule.rangeConstraintValue.trim(),
      message: rule.message.trim(),
    }));

    // Combine with existing rules for validation
    const allRules = [...existingRules, ...rules];

    // Validate rules (checks for overlaps, valid ranges, etc.)
    validateRecommendationRules(allRules);

    // If validation passes, clear any error
    if (validationError) {
      setValidationError(null);
    }
  } catch (error) {
    const errorMessage = error instanceof ValidationError
      ? error.message
      : error instanceof Error
        ? error.message
        : "An unexpected error occurred";

    if (validationError !== errorMessage) {
      setValidationError(errorMessage);
    }
  }

  // Compute valid range types for each rule based on all other rules
  const getValidRangeTypesForRule = useMemo(() => {
    return (currentIndex: number): RangeType[] => {
      // Collect all other rules (existing + current form rules, excluding the current one)
      const otherRules: Array<{ rangeType: RangeType; rangeConstraintValue: string }> = [];

      // Add existing rules from database
      existingRules.forEach((rule: RecommendationRuleInput) => {
        otherRules.push({
          rangeType: rule.rangeType as RangeType,
          rangeConstraintValue: rule.rangeConstraintValue,
        });
      });

      // Add other rules from current form
      watchedRules.forEach((rule, index) => {
        if (index !== currentIndex && rule.rangeConstraintValue) {
          otherRules.push({
            rangeType: rule.rangeType as RangeType,
            rangeConstraintValue: rule.rangeConstraintValue,
          });
        }
      });

      // Parse all other rules into ranges
      const otherParsedRanges = otherRules
        .map((rule) => parseRangeForFiltering(rule.rangeType, rule.rangeConstraintValue))
        .filter((range): range is NonNullable<typeof range> => range !== null);

      // Get the current rule's value
      const currentValue = watchedRules[currentIndex]?.rangeConstraintValue || "";

      // For each range type option, check if it would create an overlap
      const validTypes: RangeType[] = [];

      for (const option of RangeTypeOptions) {
        const rangeType = option.value as RangeType;

        // If no value entered yet, all types are potentially valid
        if (!currentValue.trim()) {
          validTypes.push(rangeType);
          continue;
        }

        // Parse the current rule with this range type
        const currentRange = parseRangeForFiltering(rangeType, currentValue);

        // If can't parse, don't filter it out (let validation handle it)
        if (!currentRange) {
          validTypes.push(rangeType);
          continue;
        }

        // Check if this range would overlap with any other range
        const wouldOverlap = otherParsedRanges.some((otherRange) =>
          rangesOverlap(currentRange, otherRange)
        );

        if (!wouldOverlap) {
          validTypes.push(rangeType);
        }
      }

      return validTypes;
    };
  }, [existingRules, watchedRules]);

  // Toggle rule expansion
  const toggleRule = (index: number) => {
    setExpandedRules((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    setIsSubmitting(true);
    setValidationError(null);

    try {
      // Convert form data to RecommendationRuleInput format
      const rules: RecommendationRuleInput[] = data.rules.map((rule) => ({
        rangeType: rule.rangeType,
        constraintType: rule.constraintType,
        rangeConstraintValue: rule.rangeConstraintValue.trim(),
        message: rule.message.trim(),
      }));

      // Combine with existing rules for validation
      const allRules = [...existingRules, ...rules];

      // Validate rules (checks for overlaps, valid ranges, etc.)
      validateRecommendationRules(allRules);

        // If we get here, validation passed
        if (existingGroupId) {
          // Update existing recommendation
          const result = await updateRecommendation(
            data.recommendationDate,
            data.cropYear,
            data.commodityType,
            existingGroupId,
            { rules }
          );

          if (!result.success) {
            setValidationError(result.message || "Failed to update recommendation");
            setIsSubmitting(false);
            return;
          }
        } else {
          // Create new recommendation
          const result = await createRecommendation({
            recommendationDate: data.recommendationDate,
            cropYear: data.cropYear,
            commodityType: data.commodityType,
            rules,
          });

          if (!result.success) {
            // Check for specific backend error message
            const errorMessage = result.message || "Failed to create recommendation";
            if (errorMessage.includes("Date Recommendation already exists")) {
              setValidationError("A recommendation already exists for this date, commodity, and crop year combination. Please select a different commodity or crop year.");
              toast({
                title: "Date Recommendation already exists"
              });
            } else {
              setValidationError(errorMessage);
            }
            setIsSubmitting(false);
            return;
          }
        }

      onSuccess?.();
    } catch (error) {
      if (error instanceof ValidationError) {
        setValidationError(error.message);
      } else {
        setValidationError(
          error instanceof Error ? error.message : "An unexpected error occurred"
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="flex gap-6 h-[calc(90vh-6rem)]">
        {/* Left Panel: Preview */}
        <div className="flex flex-col hidden md:block md:w-2/5">
          <div className="flex-1 overflow-y-auto border rounded-lg p-4 bg-muted/30 hidden md:block">
            <h3 className="text-lg font-semibold mb-4 sticky top-0 bg-muted/30 pb-2">Preview</h3>
            {watchedRules.length > 0 ? (
              <div className="space-y-2">
              {validationError && (
                <div className="p-4 border border-destructive rounded-md bg-destructive/10 hidden md:block">
                  <p className="text-sm text-destructive font-medium">{validationError}</p>
                </div>
              )}
                {watchedRules.map((rule, index) => {
                  const preview = formatRulePreview(rule);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-md border text-sm ${
                        preview
                          ? "bg-background border-border"
                          : "bg-muted/50 border-dashed border-muted-foreground/30 text-muted-foreground italic"
                      }`}
                    >
                      {preview || `Rule ${index + 1}: (incomplete)`}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm italic">No rules defined yet</p>
            )}
          </div>
        </div>

        {/* Right Panel: Form */}
        <div className="w-full md:w-3/5 flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Validation Error at Top */}
            {validationError && (
              <div className="p-4 border border-destructive rounded-md bg-destructive/10 block md:hidden">
                <p className="text-sm text-destructive font-medium">{validationError}</p>
              </div>
            )}

            {/* Recommendation Group Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Recommendation Group</h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="recommendationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date *</FormLabel>
                      <FormControl>
                        <FormInput
                          type="date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="commodityType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Commodity *</FormLabel>
                      <FormControl>
                        <FormCombobox
                          dwimOnChange
                          isModal
                          options={filteredCommodityOptions}
                          value={field.value || ""}
                          onValueChange={field.onChange}
                          placeholder="Select commodity"
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cropYear"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Crop Year *</FormLabel>
                      <FormControl>
                        <CropYearSelect
                          value={field.value?.toString() || ""}
                          onChange={(value) => field.onChange(parseInt(value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Rules Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Rules</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newIndex = fields.length;
                    append({
                      rangeType: "<=",
                      constraintType: "hedged_percent",
                      rangeConstraintValue: "",
                      message: "",
                    });
                    // Collapse all existing rules and expand only the new one
                    setExpandedRules(new Set([newIndex]));
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              </div>

              {fields.map((field, index) => {
                const isExpanded = expandedRules.has(index);
                const rule = watchedRules[index];
                const ruleSummary = rule?.rangeConstraintValue
                  ? `${formatConstraintType(rule.constraintType)} ${rule.rangeType} ${rule.rangeConstraintValue}`
                  : "Incomplete rule";
                const validRangeTypes = getValidRangeTypesForRule(index);

                return (
                  <Collapsible key={field.id} open={isExpanded} onOpenChange={() => toggleRule(index)}>
                    <Card className="overflow-hidden">
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                            <h4 className="font-medium">Rule {index + 1}</h4>
                            {!isExpanded && (
                              <span className="text-sm text-muted-foreground ml-2">
                                {ruleSummary}
                              </span>
                            )}
                          </div>
                          {fields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                remove(index);
                                setExpandedRules((prev) => {
                                  const newSet = new Set(prev);
                                  newSet.delete(index);
                                  return newSet;
                                });
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <div className="px-4 pb-4 space-y-4 border-t pt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name={`rules.${index}.rangeType`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Range Type *</FormLabel>
                                  <FormControl>
                                    <Select
                                      value={field.value}
                                      onValueChange={field.onChange}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select range type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {RangeTypeOptions.map((option) => {
                                          const isDisabled = !validRangeTypes.includes(option.value as RangeType);
                                          return (
                                            <SelectItem
                                              key={option.value}
                                              value={option.value}
                                              disabled={isDisabled}
                                            >
                                              {option.label}
                                              {isDisabled && " (would overlap)"}
                                            </SelectItem>
                                          );
                                        })}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={form.control}
                              name={`rules.${index}.constraintType`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Constraint Type *</FormLabel>
                                  <FormControl>
                                    <Select
                                      value={field.value}
                                      onValueChange={field.onChange}
                                    >
                                      <SelectTrigger>
                                        <SelectValue placeholder="Select constraint type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {ConstraintTypeOptions.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          <FormField
                            control={form.control}
                            name={`rules.${index}.rangeConstraintValue`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Range Constraint Value *</FormLabel>
                                <FormControl>
                                  <FormInput
                                    {...field}
                                    placeholder={
                                      form.watch(`rules.${index}.rangeType`) === "between"
                                        ? "e.g., 10-20"
                                        : "e.g., 50"
                                    }
                                  />
                                </FormControl>
                                <FormMessage />
                                <p className="text-xs text-muted-foreground">
                                  {form.watch(`rules.${index}.rangeType`) === "between"
                                    ? "Format: min-max (e.g., 10-20)"
                                    : "Enter a numeric value"}
                                </p>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`rules.${index}.message`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Message *</FormLabel>
                                <FormControl>
                                  <FormTextarea
                                    {...field}
                                    placeholder="Enter the recommendation message"
                                    rows={2}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                );
              })}
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            {showCancelButton && onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : existingGroupId ? "Update" : "Create"}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
